import { useCallback, useEffect, useRef, useState } from 'react';
import type { VoiceInputConfig } from '@cowbell/moo';
import { useLiveTranscription } from '../../../../hooks/useLiveTranscription';
import { useNotifications } from '../../../../_lib/notifications/useNotifications';

export const MOO_V2_VOICE_COMMIT_EVENT = 'moo.v2.voiceCommit';
export const MOO_V2_VOICE_CANCEL_EVENT = 'moo.v2.voiceCancel';

export function useVoiceInput(): VoiceInputConfig {
  const notifications = useNotifications();
  const [liveTranscript, setLiveTranscript] = useState('');
  const liveTranscriptRef = useRef('');
  liveTranscriptRef.current = liveTranscript;
  // Guards against a transcript update racing a thread/agent switch (or a
  // send that consumed the dictation) — flipped false the instant a cancel
  // fires so a WebSocket message still in flight for the abandoned session
  // can't repopulate the transcript and get committed into whichever
  // composer the user has since switched to.
  const [isSessionLive, setIsSessionLive] = useState(false);

  const handleTranscript = useCallback(
    (transcript: string) => {
      if (!isSessionLive) return;
      setLiveTranscript(transcript);
    },
    [isSessionLive]
  );

  const { isActive, isRecording, start, stop } = useLiveTranscription({
    onTranscript: handleTranscript,
    onError: (_kind, message) =>
      notifications.error('Voice input failed', message),
  });

  // When session ends for any reason (manual stop, server close, error),
  // dispatch the final transcript so Inner can commit it to the composer.
  const wasActiveRef = useRef(false);
  useEffect(() => {
    const wasActive = wasActiveRef.current;
    wasActiveRef.current = isActive;
    if (wasActive && !isActive) {
      const dictated = liveTranscriptRef.current.trim();
      setLiveTranscript('');
      if (dictated) {
        globalThis.dispatchEvent(
          new CustomEvent<string>(MOO_V2_VOICE_COMMIT_EVENT, {
            detail: dictated,
          })
        );
      }
    }
  }, [isActive]);

  // Cancel recording when Inner signals a conversation/agent switch (or the
  // panel closing). isSessionLive flips first so a message already in
  // flight can't resurrect the transcript after this runs.
  useEffect(() => {
    const handler = () => {
      setIsSessionLive(false);
      liveTranscriptRef.current = '';
      setLiveTranscript('');
      stop();
    };
    globalThis.addEventListener(MOO_V2_VOICE_CANCEL_EVENT, handler);
    return () =>
      globalThis.removeEventListener(MOO_V2_VOICE_CANCEL_EVENT, handler);
  }, [stop]);

  const onToggle = useCallback(() => {
    if (isActive) {
      stop();
    } else {
      setIsSessionLive(true);
      liveTranscriptRef.current = '';
      setLiveTranscript('');
      void start();
    }
  }, [isActive, start, stop]);

  // Stops recording and returns the trimmed transcript for the caller to
  // fold into its own send/improve text — unlike the mic button's manual
  // stop, this doesn't leave anything for the session-end effect above to
  // commit into the composer, since the caller is already using the text.
  const consumeTranscript = useCallback(() => {
    const dictated = liveTranscriptRef.current.trim();
    setIsSessionLive(false);
    liveTranscriptRef.current = '';
    setLiveTranscript('');
    stop();
    return dictated;
  }, [stop]);

  return {
    isListening: isRecording,
    isConnecting: isActive && !isRecording,
    liveTranscript,
    onToggle,
    consumeTranscript,
  };
}
